import React, { useMemo } from "react";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import moment from "moment";
import { useNavigate } from "react-router";
import { Label } from "src/components";

const RootStyle = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  boxShadow: theme.customShadows?.z8 || "0 8px 20px rgba(0,0,0,0.08)",
}));

const DetailLine = ({ icon, label, value, isLoading }) => (
  <Stack direction="row" alignItems="flex-start" spacing={0.75}>
    {isLoading ? (
      <Skeleton variant="circular" width={18} height={18} />
    ) : (
      <Box
        sx={{
          display: "inline-flex",
          color: "text.secondary",
          mt: "2px",
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" noWrap>
        {isLoading ? <Skeleton width={120} /> : value || "-"}
      </Typography>
    </Box>
  </Stack>
);

const getSafeDate = (date) =>
  date && moment(date).isValid() ? moment(date).format("MM-DD-YYYY h:mm a") : "-";

const getCustomerName = (customer = {}) =>
  [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") ||
  customer?.full_name ||
  customer?.email ||
  "-";

export default function JobCard({ item, row, isLoading, serialNumber }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const data = item || row || {};
  const jobId = data?._id || data?.id;
  const isActive = data?.is_active === true || data?.is_active === 1;

  const unreadMessagesCount = useMemo(() => {
    const messages = Array.isArray(data?.messages) ? data.messages : [];
    return messages.filter(
      (message) => message?.customerId && message?.isRead === false
    ).length;
  }, [data?.messages]);

  const badgeContent = unreadMessagesCount > 9 ? "9+" : unreadMessagesCount;

  return (
    <RootStyle
      sx={{
        borderLeft: `6px solid ${
          isLoading
            ? theme.palette.divider
            : theme.palette[isActive ? "success" : "error"].main
        }`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {isLoading ? (
          <Skeleton variant="rounded" width={52} height={52} />
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              bgcolor: isActive ? "success.lighter" : "error.lighter",
              color: isActive ? "success.dark" : "error.dark",
            }}
          >
            <WorkOutlineRoundedIcon fontSize="small" />
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {isLoading ? <Skeleton width={36} /> : `#${serialNumber}`}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {isLoading ? (
                  <Skeleton width="80%" />
                ) : (
                  data?.job_id || "Job request"
                )}
              </Typography>
            </Box>

            {isLoading ? (
              <Skeleton width={70} height={24} />
            ) : (
              <Label
                variant={theme.palette.mode === "light" ? "ghost" : "filled"}
                color={isActive ? "success" : "error"}
              >
                {isActive ? "Active" : "in-Active"}
              </Label>
            )}
          </Stack>

          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <DetailLine
              icon={<PersonOutlineRoundedIcon />}
              label="Customer"
              value={getCustomerName(data?.customer)}
              isLoading={isLoading}
            />
            <DetailLine
              icon={<Inventory2OutlinedIcon />}
              label="Product"
              value={data?.product?.name}
              isLoading={isLoading}
            />
            <DetailLine
              icon={<HomeWorkRoundedIcon />}
              label="Project Address"
              value={data?.project_address}
              isLoading={isLoading}
            />
            <DetailLine
              icon={<CalendarTodayRoundedIcon />}
              label="Created On"
              value={getSafeDate(data?.job_created)}
              isLoading={isLoading}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 1.5 }}
          >
            {isLoading ? (
              <Skeleton width={118} height={28} />
            ) : unreadMessagesCount > 0 ? (
              <Label variant="ghost" color="error">
                {badgeContent} unread
              </Label>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No unread messages
              </Typography>
            )}

            {isLoading ? (
              <Skeleton variant="circular" width={34} height={34} />
            ) : (
              <Tooltip title="View job">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/jobs/${jobId}`)}
                    disabled={!jobId}
                  >
                    <VisibilityRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Stack>
    </RootStyle>
  );
}
